import {
  BaseUserType,
  NewUserDetails,
  Role,
  UserData,
  ViewUsersRequest,
} from '../types';
import { principalServiceRequest } from './base-request';
import { ViewUsersResponse } from './user-endpoints.types';

export const getCurrentUser = async () => {
  return (await principalServiceRequest<UserData>('get', 'users/me')).data;
};

export function createPrincipalUser(
  { mandateFilters, ...userDetails }: NewUserDetails,
  principalId: string,
) {
  return principalServiceRequest<{ userId: string; inviteUrl: string }>(
    'post',
    `users/principal/${principalId}`,
    {
      mandateFilters: mandateFilters[principalId],
      ...userDetails,
    },
  );
}

export function updateGlobalUserRoles(
  userId: string,
  roles: Record<string, Role> | null,
) {
  return principalServiceRequest<void>('patch', `users/${userId}`, {
    roles: roles,
  });
}

export const searchAdUsersByName = async (searchTerm: string) => {
  return principalServiceRequest<{ results?: BaseUserType[] }>(
    'get',
    `users/azelis?searchTerm=${searchTerm}`,
  );
};

export const updateCurrentUser = async (
  newInformation: Record<string, unknown>,
) => {
  await principalServiceRequest('patch', 'users/me', newInformation);
};

export const updateUserById = async (
  userId: string,
  principalId: string,
  newInformation: Record<string, unknown>,
) => {
  await principalServiceRequest(
    'patch',
    `users/${userId}/${principalId}`,
    newInformation,
  );
};

export const deleteUsers = async (userIds: string[], principalId: string) => {
  const successfulDeletion: string[] = [];
  const failedDeletion: string[] = [];

  const resultsOfPromises = await Promise.all(
    userIds.map(async (singleUserId) => {
      try {
        await principalServiceRequest(
          'delete',
          `users/${singleUserId}/principal/${principalId}`,
        );
        return { success: true, userId: singleUserId };
      } catch (error) {
        console.error(`deleteUser for ${singleUserId} `, error);
        return { success: false, userId: singleUserId, error };
      }
    }),
  );

  //Process result after all requests are completed:
  resultsOfPromises.forEach((result) => {
    if (result.success) {
      successfulDeletion.push(result.userId);
    } else {
      failedDeletion.push(result.userId);
    }
  });
  return { successfulDeletion, failedDeletion };
};

export const viewUsers = async (
  params: ViewUsersRequest,
): Promise<ViewUsersResponse> => {
  const { allInternalUsers, currentPrincipal, userIdToView } = params;

  if (allInternalUsers) {
    const users = await principalServiceRequest<{ users: UserData[] }>(
      'get',
      'users/?emailPart=@azelis&excludeGlobal=true',
    );

    return {
      result: users.data.users,
    };
  } else if (userIdToView) {
    const user = await principalServiceRequest<UserData>(
      'get',
      `users/${userIdToView}/principal/${currentPrincipal}`,
    );

    return {
      result: user.data,
    };
  } else {
    // Get all users of current principal
    const users = await principalServiceRequest<{ users: UserData[] }>(
      'get',
      `users/principal/${currentPrincipal}`,
    );

    return {
      result: users.data.users,
    };
  }
};

export const getAllGlobalUsers = () => {
  return principalServiceRequest<UserData[]>('get', 'users/global-user');
};
